import { useEffect, useRef, useState } from "react";
import type { User } from "firebase/auth";
import { getWorkspaceMembership } from "../lib/auth";
import { WorkspaceCreation } from "./WorkspaceCreation";

/**
 * Signup gate for the first-time "no membership" screen. Asks the server
 * (GET /api/workspaces/can-create) whether this account may create a
 * workspace; only then mounts WorkspaceCreation, which provisions on mount.
 * Fails closed: any error or a non-true answer shows the invite-only message.
 * Invite-code joins are reached through onShowJoin and are unaffected.
 */
export function CreateWorkspaceGate({
  user,
  onCreated,
  onSignOut,
  onShowJoin,
}: {
  user: User;
  onCreated: (workspaceId: string, role?: "lead" | "partner") => void;
  onSignOut: () => void;
  onShowJoin: () => void;
}) {
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const onCreatedRef = useRef(onCreated);
  onCreatedRef.current = onCreated;

  useEffect(() => {
    let cancelled = false;
    async function check() {
      try {
        const idToken = await user.getIdToken();
        const res = await fetch("/api/workspaces/can-create", {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        const body = res.ok ? await res.json() : null;
        if (cancelled) return;
        if (body?.allowed !== true && user.email) {
          // Keeps the idempotency guard WorkspaceCreation has for allowed
          // accounts: an account whose App-level membership lookup came back
          // transiently null but who really has one is routed into it, not
          // told Relay is invite-only.
          const existing = await getWorkspaceMembership(user.email);
          if (cancelled) return;
          if (existing) {
            onCreatedRef.current(existing.workspaceId, existing.role);
            return;
          }
        }
        setAllowed(body?.allowed === true);
      } catch {
        if (!cancelled) setAllowed(false);
      }
    }
    check();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (allowed === true) {
    return (
      <WorkspaceCreation
        user={user}
        onCreated={onCreated}
        onSignOut={onSignOut}
        onShowJoin={onShowJoin}
        checkExistingMembership
      />
    );
  }

  return (
    <div
      className="h-screen w-screen flex items-center justify-center"
      style={{ background: "var(--bg)", color: "var(--text)" }}
    >
      <div
        className="flex flex-col items-center gap-4 px-6 py-10"
        style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, maxWidth: 420, width: "100%" }}
      >
        <span className="font-mono text-lg lowercase tracking-tight" style={{ color: "var(--text)" }}>
          relay
        </span>
        {allowed === null ? (
          <div className="text-xs" style={{ color: "var(--text2)" }}>Checking your access…</div>
        ) : (
          <>
            <div className="text-sm font-medium text-center" style={{ color: "var(--text)" }}>
              Relay is invite-only right now. Ask a workspace Lead to invite you.
            </div>
            <button
              onClick={onShowJoin}
              style={{ background: "transparent", color: "var(--accent)", border: 0, padding: "4px 0", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}
            >
              Have an invite code instead?
            </button>
            <button
              onClick={onSignOut}
              style={{ background: "var(--accent)", color: "#fff", border: 0, borderRadius: 6, padding: "8px 18px", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}
            >
              Sign out
            </button>
          </>
        )}
      </div>
    </div>
  );
}
